import { iMessage, postMessageSystem } from "modules-core/postMessageSystem"
import { syrinscape } from "app/common/syrinscape"

class SyrinscapeEvent {
	eventName: string
	listeners: (EventListenerOrEventListenerObject)[]

	constructor(name: string, listener?: EventListenerOrEventListenerObject) {
		this.listeners = []
		if (listener) {
			this.listeners.push(listener)
		}
		this.eventName = `syrinscape.${name}`
	}

	addListener(listener: EventListenerOrEventListenerObject) {
		this.listeners.push(listener)
		document.addEventListener(this.eventName, listener)
		// Return unbind function.
		return () => {
			document.removeEventListener(this.eventName, listener)
		}
	}

	delete() {
		this.listeners.forEach((listener) => {
			document.removeEventListener(this.eventName, listener)
		})
		delete syrinscape.events[this.eventName]
	}

	dispatch(detail?: any) {
		const event = new CustomEvent(this.eventName, {
			detail: detail || null,
		})
		document.dispatchEvent(event)
		postMessageSystem.sendMessage('syrinscape.dispatchEvent', {
			eventName: this.eventName,
			eventDetail: detail,
		})
	}
}

interface SyrinscapeEvents {
	[key: string]: SyrinscapeEvent
}

// Add events to global syrinscape object and export for other modules.
syrinscape.events ??= {} as SyrinscapeEvents
export const events: SyrinscapeEvents = syrinscape.events

const createEventSystem = () => {
	postMessageSystem.onMessage('syrinscape.dispatchEvent').addListener(
		(msg: iMessage) => {
			const event = new CustomEvent(msg.params.eventName, {
				detail: msg.params.detail || null,
			})
			document.dispatchEvent(event)
		}
	)

	let getOrCreate = (eventName: string) => {
		let event = syrinscape.events[eventName]
		if (typeof event === 'undefined') {
			syrinscape.events[eventName] = new SyrinscapeEvent(eventName)
		}
		return syrinscape.events[eventName]
	}

	return {
		add: (eventName: string, listener?: EventListenerOrEventListenerObject) => {
			return getOrCreate(eventName).addListener(listener)
		},

		delete: (eventName: string) => {
			if (!syrinscape.events[eventName]) {
				return
			}
			syrinscape.events[eventName].delete()
		},

		dispatch: (eventName: string, detail?: any) => {
			getOrCreate(eventName).dispatch(detail)
		},
	}
}

export type EventSystem = ReturnType<typeof createEventSystem>

export const eventSystem = createEventSystem()
export default eventSystem
